/**
 * Utility functions for Roaming Days feature
 */

export interface RoamingDayConfig {
  condition_a: { con_operator: string; value: number };
  condition_b: { con_operator: string; value: number } | null;
}

export interface RoamingDaysByCountry {
  [iso: string]: RoamingDayConfig;
}

export interface RoamingDaysPayload {
  [iso: string]: {
    condition_a: { con_operator: string; value: number };
    condition_b?: { con_operator: string; value: number };
  };
}

/**
 * Formats roaming days from internal state to API payload format
 * Converts ISO codes to lowercase and removes null condition_b entries
 * 
 * @param roamingDaysByCountry - Internal roaming days state
 * @returns Formatted payload for API
 * 
 * @example
 * Input:
 * {
 *   "IN": { condition_a: { con_operator: ">=", value: 1 }, condition_b: { con_operator: "<=", value: 30 } },
 *   "SA": { condition_a: { con_operator: "=", value: 5 }, condition_b: null }
 * }
 * 
 * Output:
 * {
 *   "in": { condition_a: { con_operator: ">=", value: 1 }, condition_b: { con_operator: "<=", value: 30 } },
 *   "sa": { condition_a: { con_operator: "=", value: 5 } }
 * }
 */
export const formatRoamingDaysPayload = (
  roamingDaysByCountry: RoamingDaysByCountry
): RoamingDaysPayload => {
  const payload: RoamingDaysPayload = {};

  Object.keys(roamingDaysByCountry).forEach((iso) => {
    const config = roamingDaysByCountry[iso];
    const lowercaseISO = iso.toLowerCase();

    payload[lowercaseISO] = {
      condition_a: {
        con_operator: config.condition_a.con_operator,
        value: config.condition_a.value,
      },
    };

    // Only include condition_b if it exists (not null)
    if (config.condition_b !== null) {
      payload[lowercaseISO].condition_b = {
        con_operator: config.condition_b.con_operator,
        value: config.condition_b.value,
      };
    }
  });

  return payload;
};

/**
 * Parses roaming days from API response to internal state format
 * Converts lowercase ISO codes to uppercase
 * 
 * @param apiRoamingDays - Roaming days from API response
 * @returns Formatted roaming days for internal state
 * 
 * @example
 * Input (from API):
 * {
 *   "in": { condition_a: { con_operator: ">=", value: 5 }, condition_b: { con_operator: "<", value: 10 } }
 * }
 * 
 * Output (for state):
 * {
 *   "IN": { condition_a: { con_operator: ">=", value: 5 }, condition_b: { con_operator: "<", value: 10 } }
 * }
 */
export const parseRoamingDaysFromAPI = (
  apiRoamingDays: RoamingDaysPayload
): RoamingDaysByCountry => {
  const formatted: RoamingDaysByCountry = {};

  Object.keys(apiRoamingDays).forEach((iso) => {
    const uppercaseISO = iso.toUpperCase();
    const config = apiRoamingDays[iso];

    formatted[uppercaseISO] = {
      condition_a: config.condition_a,
      condition_b: config.condition_b || null,
    };
  });

  return formatted;
};

/**
 * Initializes default roaming days configuration for a country
 * Default: >= 1 AND <= 30
 * 
 * @param iso - Country ISO code
 * @returns Default roaming day config
 */
export const getDefaultRoamingDayConfig = (): RoamingDayConfig => {
  return {
    condition_a: { con_operator: ">=", value: 1 },
    condition_b: { con_operator: "<=", value: 30 },
  };
};

/**
 * Validates roaming day configuration
 * 
 * @param config - Roaming day config to validate
 * @returns Validation result with error message if invalid
 */
export const validateRoamingDayConfig = (
  config: RoamingDayConfig
): { valid: boolean; error?: string } => {
  // Validate condition_a
  if (![">=", ">", "="].includes(config.condition_a.con_operator)) {
    return {
      valid: false,
      error: "Invalid operator for condition_a. Must be >=, >, or =",
    };
  }

  if (config.condition_a.value < 0 || config.condition_a.value > 30) {
    return {
      valid: false,
      error: "condition_a value must be between 0 and 30",
    };
  }

  // If condition_a is "=", condition_b should be null
  if (config.condition_a.con_operator === "=" && config.condition_b !== null) {
    return {
      valid: false,
      error: 'condition_b must be null when condition_a operator is "="',
    };
  }

  // Validate condition_b if it exists
  if (config.condition_b !== null) {
    if (!["<", "<="].includes(config.condition_b.con_operator)) {
      return {
        valid: false,
        error: "Invalid operator for condition_b. Must be < or <=",
      };
    }

    if (config.condition_b.value < 0 || config.condition_b.value > 30) {
      return {
        valid: false,
        error: "condition_b value must be between 0 and 30",
      };
    }

    // condition_b value must be at least condition_a value + 2
    if (config.condition_b.value <= config.condition_a.value + 1) {
      return {
        valid: false,
        error: `condition_b value must be at least ${
          config.condition_a.value + 2
        }`,
      };
    }
  }

  return { valid: true };
};

/**
 * Validates all roaming day configurations
 * 
 * @param roamingDaysByCountry - All roaming day configs
 * @returns Overall validation result
 */
export const validateAllRoamingDays = (
  roamingDaysByCountry: RoamingDaysByCountry
): { valid: boolean; errors: { [iso: string]: string } } => {
  const errors: { [iso: string]: string } = {};

  Object.keys(roamingDaysByCountry).forEach((iso) => {
    const config = roamingDaysByCountry[iso];
    const validation = validateRoamingDayConfig(config);

    if (!validation.valid && validation.error) {
      errors[iso] = validation.error;
    }
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Example operator display labels
 */
export const OPERATOR_LABELS: { [key: string]: string } = {
  ">": "Greater Than",
  ">=": "Greater Than Equal To",
  "=": "Equal To",
  "<": "Lesser Than",
  "<=": "Lesser Than Equal To",
};